// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-263 — the `postMessage` protocol for the verifier-served confirm
 * embed.
 *
 * The verifier serves `GET /embed/confirm/{id}` as an `<iframe>` inside
 * the user-app's vault-creation modal. Independence comes from origin
 * isolation: the contract is rendered by the verifier's OWN origin, never
 * by user-app (this gate defends against a compromised user-app — COB-245
 * — so a user-app-rendered contract would defeat the purpose).
 *
 * Direction of trust in this flow is the INVERSE of the `sign-via-user-app`
 * popup: here the verifier embed is the CHILD, and the user-app is the
 * PARENT that holds the in-context key-agent and signs the ownership
 * challenge. The embed asks the parent to sign; the parent reconstructs
 * the canonical `cobault-verifier-ownership-v1:{vault}:{nonce}` message
 * itself and NEVER signs a caller-supplied body.
 *
 * This module is TYPES + GUARDS ONLY, so it can be mirrored verbatim into
 * user-app (copied, not imported — no cross-repo source deps). Every
 * message carries `type: 'cobault-embed-confirm-v1'`; a receiver MUST also
 * pin `event.origin` to the expected peer origin before trusting a
 * message (the type check alone is not an origin check).
 */

export const EMBED_PROTOCOL = 'cobault-embed-confirm-v1';

// ── child → parent (verifier embed → user-app host) ─────────────────

/** The embed has mounted and attached its listener. Lets the host show a
 *  "loading verifier…" state until the frame is alive. */
export interface EmbedReadyMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'ready';
  vault_id: string;
}

/** Please sign the ownership challenge for `vault_id` over `nonce_hex`.
 *  The parent reconstructs the canonical message from these two fields —
 *  it does not receive (and must not sign) a pre-built message body. */
export interface EmbedRequestSignatureMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'request-signature';
  vault_id: string;
  nonce_hex: string;
}

/** Content height changed — the host should resize the iframe to `height`
 *  CSS pixels so the embed never scrolls inside its own frame. */
export interface EmbedResizeMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'resize';
  height: number;
}

/** The owner confirmed the contract on the verifier. **Advisory only** —
 *  the deposit-address reveal stays gated server-side on the verifier's
 *  ack-status (fail-closed). A spoofed `confirmed` must reveal nothing;
 *  the host uses it purely as a UX signal (close the modal, refetch). */
export interface EmbedConfirmedMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'confirmed';
  vault_id: string;
  contract_hash_hex: string;
}

/** An unrecoverable error inside the embed (advisory — the host may show
 *  a fallback + the direct verifier link). */
export interface EmbedErrorMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'error';
  code: string;
  message: string;
}

export type EmbedChildMessage =
  | EmbedReadyMessage
  | EmbedRequestSignatureMessage
  | EmbedResizeMessage
  | EmbedConfirmedMessage
  | EmbedErrorMessage;

// ── parent → child (user-app host → verifier embed) ─────────────────

/** The parent's DER-encoded ECDSA signature over the canonical ownership
 *  message it reconstructed for `(vault_id, nonce_hex)`. */
export interface EmbedSignatureMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'signature';
  vault_id: string;
  nonce_hex: string;
  signature_der_hex: string;
}

/** The parent could not produce a signature (user declined, no session,
 *  key-agent locked, etc.). `reason` is a short machine-ish code the
 *  embed surfaces without leaking parent-side detail. */
export interface EmbedSignatureErrorMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'signature-error';
  vault_id: string;
  nonce_hex: string;
  reason: string;
}

export type EmbedParentMessage =
  | EmbedSignatureMessage
  | EmbedSignatureErrorMessage;

// ── child-message constructors (typed envelope builders) ────────────

export function readyMessage(vaultId: string): EmbedReadyMessage {
  return { type: EMBED_PROTOCOL, kind: 'ready', vault_id: vaultId };
}

export function requestSignatureMessage(
  vaultId: string,
  nonceHex: string,
): EmbedRequestSignatureMessage {
  return {
    type: EMBED_PROTOCOL,
    kind: 'request-signature',
    vault_id: vaultId,
    nonce_hex: nonceHex,
  };
}

export function resizeMessage(height: number): EmbedResizeMessage {
  return { type: EMBED_PROTOCOL, kind: 'resize', height };
}

export function confirmedMessage(
  vaultId: string,
  contractHashHex: string,
): EmbedConfirmedMessage {
  return {
    type: EMBED_PROTOCOL,
    kind: 'confirmed',
    vault_id: vaultId,
    contract_hash_hex: contractHashHex,
  };
}

export function errorMessage(code: string, message: string): EmbedErrorMessage {
  return { type: EMBED_PROTOCOL, kind: 'error', code, message };
}

// ── parent → child guards (used by the embed) ───────────────────────

const HEX_RE = /^[0-9a-f]+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * True iff `value` is a well-formed `signature` reply for exactly the
 * `(vault, nonce)` the embed asked to sign. Callers MUST additionally
 * have checked `event.origin === <expected parent origin>` before calling
 * this — a matching shape from the wrong origin is still hostile.
 */
export function isEmbedSignatureFor(
  value: unknown,
  vault: string,
  nonce: string,
): value is EmbedSignatureMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'signature' &&
    value.vault_id === vault &&
    value.nonce_hex === nonce &&
    typeof value.signature_der_hex === 'string' &&
    HEX_RE.test(value.signature_der_hex)
  );
}

/** True iff `value` is a `signature-error` reply for the pending
 *  `(vault, nonce)`. Origin must be checked separately, as above. */
export function isEmbedSignatureErrorFor(
  value: unknown,
  vault: string,
  nonce: string,
): value is EmbedSignatureErrorMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'signature-error' &&
    value.vault_id === vault &&
    value.nonce_hex === nonce &&
    typeof value.reason === 'string'
  );
}
